import { CommonModule } from '@angular/common';
import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {extractErrorMessage, goBack} from 'src/app/common/common';
import { ApiService } from 'src/app/services/api.service';
import moment from 'moment';
import {SystemPropertiesDto} from "../../../../model/systemPropeties.model";

@Component({
  selector: 'app-create-system-properties-index',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './create-system-properties.component.html',
  styleUrl: './create-system-properties.component.scss'
})
export class CreateSystemPropertiesComponent implements OnInit, OnDestroy {
  public editing: boolean = false;
  public router: Router;
  public error: string | null = null;

  unsubscribe = new Subject<void>();

  // Form controls
  systemPropertyForm = new FormGroup({
    propertyKeyId: new FormControl<string>('', [Validators.required]),
    propertyType: new FormControl<string | undefined>('string', [Validators.required]),
    propertyValue: new FormControl<string>('', [Validators.required]),
  });

  public property: SystemPropertiesDto = {
    key: '',
    type: 'string',
    value: '',
  };

  constructor(
    private injector: Injector,
    private systemPropertiesService: ApiService
  ) {
    this.router = injector.get(Router);
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.editing = true;
        this.property.key = event.snapshot.params.id;
      }
    });
  }

  ngOnInit(): void {
    this.setupFormSubscriptions();

    if (this.editing) {
      this.loadProperty();
    }
  }

  setupFormSubscriptions() {
    this.systemPropertyForm.controls.propertyKeyId.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        this.property.key = val || '';
      });

    this.systemPropertyForm.controls.propertyType.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        this.property.type = val || 'string';
      });

    this.systemPropertyForm.controls.propertyValue.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        this.property.value = val || '';
      });
  }

  loadProperty() {
    this.systemPropertiesService.getSystemProperties(1).subscribe({
      next: (response: any) => {
        const propertyData = response.find((props: any) => props.key === this.property.key);
        if (propertyData) {
          this.property = {
            key: propertyData.key,
            type: propertyData.type || 'string',
            value: propertyData.value.toString()
          };
          this.setFormValues();
        }
      },
      error: (err) => {
        console.error(err);
        this.error = extractErrorMessage(err);
      }
    });
  }

  setFormValues() {
    this.systemPropertyForm.controls.propertyKeyId.setValue(this.property.key);
    this.systemPropertyForm.controls.propertyType.setValue(this.property.type);
    this.systemPropertyForm.controls.propertyValue.setValue(this.property.value);

    if (this.editing) {
      this.systemPropertyForm.controls.propertyKeyId.disable();
      this.systemPropertyForm.controls.propertyType.disable();
    }
  }

  save() {
    if (this.systemPropertyForm.invalid) {
      this.error = 'Please fill out all fields correctly and try again.';
      return;
    }

    if (!this.property.type) {
      this.error = 'Please select a property type.';
      return;
    }

    if (!this.validatePropertyValue()) {
      return;
    }

    if (this.editing) {
      this.systemPropertiesService.editSystemProperties(this.property).subscribe({
        next: () => goBack('/systemProperties', this.router),
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err);
        }
      });
    } else {
      this.systemPropertiesService.createSystemProperties(this.property).subscribe({
        next: () => goBack('/systemProperties', this.router),
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err);
        }
      });
    }
  }

  validatePropertyValue(): boolean {
    const value = this.property.value;

    switch (this.property.type) {
      case 'int':
        if (isNaN(Number(value))) {
          this.error = 'Type is integer, but value is not a valid number.';
          return false;
        }
        break;
      case 'bool':
        if (!['true', 'false'].includes(value)) {
          this.error = 'Type is boolean, but value is not "true" or "false".';
          return false;
        }
        break;
      case 'duration':
        if (!this.isValidIsoDuration(value)) {
          this.error = 'Type is duration, but value is not a valid ISO duration.';
          return false;
        }
        break;
      case 'dttm':
        if (!this.isValidIsoDate(value)) {
          this.error = 'Type is date, but value is not a valid ISO date.';
          return false;
        }
        break;
      case 'time':
        if (!this.isValidIsoTime(value)) {
          this.error = 'Type is time, but value is not a valid ISO time.';
          return false;
        }
        break;
      case 'string':
        if (value.length > 255) {
          this.error = 'Type is string, but value is over 255 characters.';
          return false;
        }
        break;
    }

    return true;
  }

  isValidIsoDuration(durationString: string): boolean {
    const ISO_DURATION_REGEX = /P(\d+Y)?(\d+M)?(\d+D)?(T(\d+H)?(\d+M)?(\d+S)?)?/;
    const matchGroup = durationString.match(ISO_DURATION_REGEX);
    return !!matchGroup && matchGroup.length >= 1 && matchGroup[0] === durationString;
  }

  isValidIsoDate(dateString: string): boolean {
    return moment(dateString, moment.ISO_8601).isValid() ||
      moment(dateString, "YYYY-MM-DDZ", true).isValid();
  }

  isValidIsoTime(timeString: string): boolean {
    const ISO_TIME_REGEX = /\d{2}:\d{2}(:\d{2})?/;
    const matchGroup = timeString.match(ISO_TIME_REGEX);
    return !!matchGroup && matchGroup.length >= 1 && matchGroup[0] === timeString;
  }

  backPage(url: string, router: Router) {
    goBack(url, router);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
